import React, { useState } from 'react';
import { useParams, useNavigate} from 'react-router-dom';
import { useQuery, useMutation, useQueryClient} from 'react-query';
import { Box, Button, Dialog, DialogTitle, DialogContent, TextField, Typography, Divider, Tooltip} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CircularProgress from '@mui/material/CircularProgress';
import {
    EmailShareButton, ViberIcon, EmailIcon, ViberShareButton,
    FacebookMessengerIcon, FacebookMessengerShareButton,
    FacebookShareButton, FacebookIcon, TelegramIcon, TelegramShareButton
} from "react-share";
import { getOrCreateUserId } from './userUtils';
import { fetchProductList, repopulate } from './listService';
import ProductCard from './ProductCard';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';

const ListDetails = () => {
    const { id } = useParams();
    const [open, setOpen] = useState(false);
    const url = "https://fenyofa.com/lists/" + id;

    const navigate = useNavigate();

    const queryClient = useQueryClient();

    const { isLoading, error, data: productList } = useQuery({
        queryKey: ['lists', id],
        queryFn: () => fetchProductList(id),
    });

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(url);
    };

    const repopulateMutation = useMutation(
        (listId) => repopulate(listId),
        {
            onSuccess: (candidates, variables) => {
                queryClient.invalidateQueries({ queryKey: ['lists', id] });
                navigate(`/lists/${id}/swipe`)
            }
        }
    )

    if (isLoading) return <CircularProgress />;
    if (error) {
        console.error('Failed to fetch product list:', error);
        return 
    }

    const isMine = productList.userId === getOrCreateUserId();

    return (
        <Box>
            {isMine && (
                <Box justifyContent="center" display="flex" gap={4} mb={4}>
                    <Button variant="contained" onClick={() => repopulateMutation.mutate(productList.id)} sx={{flex: 1, maxWidth: 250}}>Lista tovább szűkítése</Button>
                    <Tooltip title="Ide kattintva meg tudod osztani ismerőseiddel a listát. Kérd meg őket, hogy szavazzanak a Te általad kiválasztott fák közül, a szívecskékre kattintva! A szavazatok megjelennek a listádban szereplő fenyők adatlapjánál!" PopperProps={{disablePortal: true}}>
                        <Button variant="contained" onClick={handleOpen} sx={{flex: 1, maxWidth: 250}} endIcon={<ShareIcon/>}>Lista megosztása</Button>
                    </Tooltip>
                    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" disablePortal>
                        <DialogTitle>
                            <Typography variant="h5" align="center">{productList.name} megosztása</Typography>
                        </DialogTitle>
                        <DialogContent>
                            {/* Share Buttons */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: { xs: 1, sm: 2 }, // Adjust gap for smaller screens
                                    textAlign: 'center',
                                    mb: 2, // Add margin for spacing
                                }}
                            >
                                <FacebookShareButton url={url}><FacebookIcon round size={40} /></FacebookShareButton>
                                <FacebookMessengerShareButton url={url} appId="948419277105766">
                                    <FacebookMessengerIcon round size={40} />
                                </FacebookMessengerShareButton>
                                <ViberShareButton url={url}><ViberIcon round size={40} /></ViberShareButton>
                                <TelegramShareButton url={url}><TelegramIcon round size={40} /></TelegramShareButton>
                                <EmailShareButton url={url}><EmailIcon round size={40} /></EmailShareButton>
                            </Box>

                            {/* Divider */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
                                <Divider
                                    sx={{
                                        width: "50%",
                                        "&::before, &::after": { borderColor: "#808080" },
                                    }}
                                >
                                    vagy
                                </Divider>
                            </Box>

                            {/* Copy Link Section */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' }, // Stack on small screens
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    value={url}
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                    sx={{ flexGrow: 1 }}
                                />
                                <Button
                                    startIcon={<LinkIcon />}
                                    variant="contained"
                                    onClick={handleCopy}
                                    sx={{ width: { xs: '100%', sm: 'auto' } }} // Full width on small screens
                                >
                                    Link másolása
                                </Button>
                            </Box>
                        </DialogContent>
                    </Dialog>

                </Box>
            )}
            <Grid container spacing={4}>
                {productList.products.map((item, index) => (
                    <Grid size={{ xs: 12, sm:6, md:4 }} key={index}>
                        <ProductCard productList={productList} product={item} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ListDetails;
